/** Same name de-dupe as Compensation / Base Amount workflow steps. */
function uniqueIndividualsByName<T extends { id: string; name: string }>(individuals: T[]): T[] {
  const names = Array.from(new Set(individuals.map((i) => i.name)));
  return names.map((name) => individuals.find((i) => i.name === name)).filter((row): row is T => row != null);
}

function compensationRowHasRequiredFields(row: unknown): boolean {
  if (row == null || typeof row !== 'object') return false;
  const r = row as Record<string, unknown>;
  const startDate = String(r.startDate ?? '').trim();
  const baseRaw = String(r.baseSalary ?? '')
    .replace(/,/g, '')
    .trim();
  const baseNum = parseFloat(baseRaw);
  return startDate.length > 0 && baseRaw.length > 0 && Number.isFinite(baseNum) && baseNum >= 0;
}

/** Continue / sidebar “done” for the Compensation step (requires start date + base salary per listed individual). */
export function isCompensationStepComplete(formData: {
  individuals?: Array<{ id: string; name: string }>;
  compensation?: Record<string, unknown>;
}): boolean {
  const individuals = formData.individuals;
  if (!Array.isArray(individuals) || individuals.length === 0) return false;
  const unique = uniqueIndividualsByName(individuals);
  if (unique.length === 0) return false;
  const comp = formData.compensation || {};
  return unique.every((ind) => compensationRowHasRequiredFields(comp[ind.id]));
}

/** Use with local `data` state inside CompensationForm (before parent `formData` catches up). */
export function isCompensationDataCompleteForIndividuals(
  uniqueIndividuals: Array<{ id: string }>,
  compensation: Record<string, unknown>
): boolean {
  if (uniqueIndividuals.length === 0) return false;
  return uniqueIndividuals.every((ind) => compensationRowHasRequiredFields(compensation[ind.id]));
}

/** Single start date per individual: `compensation[id].startDate`, mirrored in `baseAmountData` for API JSON. */
export function getSharedStartDate(
  formData: {
    compensation?: Record<string, unknown>;
    baseAmountData?: Record<string, unknown>;
  },
  id: string
): string {
  const c = formData.compensation?.[id] as Record<string, unknown> | undefined;
  const b = formData.baseAmountData?.[id] as Record<string, unknown> | undefined;
  return String(c?.startDate ?? b?.startDate ?? '').trim();
}

function hasSharedStartDate(
  formData: { compensation?: Record<string, unknown>; baseAmountData?: Record<string, unknown> },
  id: string
): boolean {
  return getSharedStartDate(formData, id).length > 0;
}

/** Sidebar / list: Base Amount step complete when every listed individual has a shared start date. */
export function isBaseAmountStepComplete(formData: {
  individuals?: Array<{ id: string; name: string }>;
  compensation?: Record<string, unknown>;
  baseAmountData?: Record<string, unknown>;
}): boolean {
  const individuals = formData.individuals;
  if (!Array.isArray(individuals) || individuals.length === 0) return false;
  const unique = uniqueIndividualsByName(individuals);
  if (unique.length === 0) return false;
  return unique.every((ind) => hasSharedStartDate(formData, ind.id));
}

/** Use inside BaseAmount with full `formData` (not `baseAmountData` alone). */
export function isBaseAmountDataCompleteForIndividuals(
  uniqueIndividuals: Array<{ id: string }>,
  formData: { compensation?: Record<string, unknown>; baseAmountData?: Record<string, unknown> }
): boolean {
  if (uniqueIndividuals.length === 0) return false;
  return uniqueIndividuals.every((ind) => hasSharedStartDate(formData, ind.id));
}
